import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { getPostAuthLanding } from "@/features/auth/utils/post-auth-redirect";
import { FloatPanel, StudioButton } from "@/features/studio/theme";

// The backend's base cooldown between OTP sends (RATE_LIMIT.OTP.COOLDOWN_BASE_S).
// The send-otp success response carries no cooldown, so this is the starting
// value; a 429, which does carry the real one, corrects it.
const RESEND_COOLDOWN_SECONDS = 60;
const EMPTY_OTP = ["", "", "", "", "", ""];

interface OtpFailure {
	status?: number;
	/** data.code from the backend: OTP_INVALID, OTP_EXPIRED or OTP_NOT_FOUND. */
	code?: string;
	/** From data.retryAfterSeconds, else the Retry-After header, else the base cooldown. */
	retryAfterSeconds: number;
}

interface OtpApiError {
	response?: {
		status?: number;
		headers?: Record<string, unknown>;
		data?: { data?: { code?: string; retryAfterSeconds?: number } | null };
	};
}

function readOtpFailure(err: unknown): OtpFailure {
	const response = (err as OtpApiError | null)?.response;
	const fromBody = response?.data?.data?.retryAfterSeconds;
	const fromHeader = Number(response?.headers?.["retry-after"]);
	let retryAfterSeconds = RESEND_COOLDOWN_SECONDS;
	if (typeof fromBody === "number" && fromBody > 0) {
		retryAfterSeconds = fromBody;
	} else if (Number.isFinite(fromHeader) && fromHeader > 0) {
		retryAfterSeconds = fromHeader;
	}
	return {
		status: response?.status,
		code: response?.data?.data?.code,
		retryAfterSeconds,
	};
}

export function VerifyOtpPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const phone = location.state?.phone || "";

	const {
		verifyOtp,
		sendOtp,
		isLoading,
		error,
		clearError,
		resetOtpState,
		needsOnboarding,
		needsProfileSetup,
		getUserType,
		isAuthenticated,
	} = useAuthStore();

	const [otp, setOtp] = useState(EMPTY_OTP);
	const [resendTimer, setResendTimer] = useState(RESEND_COOLDOWN_SECONDS);
	// Resend has its own flag: the store's isLoading is shared with verify.
	const [isResending, setIsResending] = useState(false);
	// A translated message for a failure recognised by code or status; any other
	// failure falls back to the store's error.
	const [failureMessage, setFailureMessage] = useState<string | null>(null);
	const [focusResend, setFocusResend] = useState(false);
	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
	const resendButtonRef = useRef<HTMLButtonElement | null>(null);
	const inlineError = failureMessage ?? error;
	const showVerifying = isLoading && !isResending;

	// Redirect if already authenticated
	useEffect(() => {
		if (isAuthenticated()) {
			if (needsOnboarding()) {
				navigate("/onboarding", { replace: true });
			} else if (needsProfileSetup()) {
				navigate(`/onboarding/${getUserType()}`, { replace: true });
			} else {
				navigate(getPostAuthLanding(getUserType()), { replace: true });
			}
		}
	}, [
		isAuthenticated,
		needsOnboarding,
		needsProfileSetup,
		getUserType,
		navigate,
	]);

	// Redirect if no phone
	useEffect(() => {
		if (!phone) {
			navigate("/login");
		}
	}, [phone, navigate]);

	// Countdown timer for resend
	useEffect(() => {
		if (resendTimer > 0) {
			const timer = setTimeout(() => setResendTimer((t) => t - 1), 1000);
			return () => clearTimeout(timer);
		}
	}, [resendTimer]);

	// Auto-focus first OTP input on mount
	useEffect(() => {
		inputRefs.current[0]?.focus();
	}, []);

	// The Resend button only exists once the timer is 0, so focus it after the
	// render that shows it.
	useEffect(() => {
		if (focusResend && resendTimer === 0) {
			resendButtonRef.current?.focus();
			setFocusResend(false);
		}
	}, [focusResend, resendTimer]);

	const handleOtpChange = (index: number, value: string) => {
		clearError();
		setFailureMessage(null);

		// Only allow single digit
		if (value.length > 1) {
			value = value.slice(-1);
		}

		// Update OTP array
		const newOtp = [...otp];
		newOtp[index] = value;
		setOtp(newOtp);

		// Auto-focus next input
		if (value && index < 5) {
			inputRefs.current[index + 1]?.focus();
		}

		// Auto-submit when all digits entered
		if (newOtp.every((digit) => digit) && newOtp.join("").length === 6) {
			handleVerify(newOtp.join(""));
		}
	};

	const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
		// Handle backspace
		if (e.key === "Backspace" && !otp[index] && index > 0) {
			inputRefs.current[index - 1]?.focus();
		}
	};

	const handlePaste = (e: React.ClipboardEvent) => {
		e.preventDefault();
		const pastedData = e.clipboardData
			.getData("text")
			.replace(/\D/g, "")
			.slice(0, 6);
		if (pastedData.length === 6) {
			const newOtp = pastedData.split("");
			setOtp(newOtp);
			handleVerify(pastedData);
		}
	};

	const handleVerify = async (otpValue: string) => {
		try {
			await verifyOtp(phone, otpValue);
			toast.success(t("auth.loginSuccess"));
			const state = location.state as { redirectTo?: string } | null;
			const redirectTo = state?.redirectTo;
			if (redirectTo) {
				navigate(redirectTo);
			} else if (needsOnboarding()) {
				navigate("/onboarding");
			} else if (needsProfileSetup()) {
				navigate(`/onboarding/${getUserType()}`);
			} else {
				navigate(getPostAuthLanding(getUserType()));
			}
		} catch (err) {
			// The message is chosen from the backend's code, not its text. A
			// failure with no known code falls back to the store's error.
			const failure = readOtpFailure(err);
			setOtp(EMPTY_OTP);
			if (failure.status === 429) {
				// Locked out after too many wrong codes; the backend says how long.
				setResendTimer(failure.retryAfterSeconds);
				setFailureMessage(
					t("auth.otpErrors.locked", { seconds: failure.retryAfterSeconds }),
				);
			} else if (failure.code === "OTP_EXPIRED") {
				setFailureMessage(t("auth.otpErrors.expired"));
				// The code outlived its expiry, so the base cooldown is long over. A
				// longer one may still be running; the 429 on click then says so.
				setResendTimer(0);
				setFocusResend(true);
				return;
			} else if (failure.code === "OTP_INVALID") {
				setFailureMessage(t("auth.otpErrors.invalid"));
			} else if (failure.code === "OTP_NOT_FOUND") {
				setFailureMessage(t("auth.otpErrors.notFound"));
			} else {
				setFailureMessage(null);
			}
			inputRefs.current[0]?.focus();
		}
	};

	const handleResend = async () => {
		setFailureMessage(null);
		setIsResending(true);
		try {
			await sendOtp(phone);
			toast.success(t("auth.otpResent"));
			setResendTimer(RESEND_COOLDOWN_SECONDS);
			setOtp(EMPTY_OTP);
			inputRefs.current[0]?.focus();
		} catch (err) {
			// Any failure is shown inline; the API client's own toast is skipped for
			// this request. A 429 also tells us how long the cooldown really is.
			const failure = readOtpFailure(err);
			if (failure.status === 429) {
				setResendTimer(failure.retryAfterSeconds);
				setFailureMessage(
					t("auth.otpErrors.resendWait", {
						seconds: failure.retryAfterSeconds,
					}),
				);
			}
		} finally {
			setIsResending(false);
		}
	};

	const handleBack = () => {
		resetOtpState();
		navigate("/login");
	};

	return (
		<div className="flex min-h-screen flex-col items-center justify-center px-4 bg-dashboard-warm-350">
			<div className="w-full max-w-md">
				{/* Header */}
				<div className="mb-8 text-center">
					<h1 className="font-display font-semibold text-[32px] text-dashboard-text mb-2">
						{t("auth.verifyOtp")}
					</h1>
					<p className="font-body text-sm text-dashboard-muted">
						{t("auth.enterOtpCode")}
						<br />
						<span className="font-medium">{phone}</span>
					</p>
				</div>

				{/* OTP Card */}
				<FloatPanel elevation={2} padding="40px 32px">
					{/* OTP Inputs */}
					<div
						className="flex justify-center gap-1.5 sm:gap-2"
						onPaste={handlePaste}
					>
						{otp.map((digit, index) => (
							<input
								// biome-ignore lint/suspicious/noArrayIndexKey: OTP inputs are positional
								key={`otp-${index}`}
								ref={(el) => {
									inputRefs.current[index] = el;
								}}
								type="text"
								inputMode="numeric"
								value={digit}
								onChange={(e) =>
									handleOtpChange(index, e.target.value.replace(/\D/g, ""))
								}
								onKeyDown={(e) => handleKeyDown(index, e)}
								className="h-11 flex-1 min-w-0 sm:h-14 sm:w-12 sm:flex-none text-center text-lg sm:text-xl font-semibold font-body text-dashboard-text bg-dashboard-warm-450 border border-dashboard-border rounded-[7px] outline-none focus:border-dashboard-accent focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--color-dashboard-accent)_12%,transparent)] transition-all duration-200"
								maxLength={1}
							/>
						))}
					</div>

					{/* Error Display */}
					{inlineError && (
						<div
							role="alert"
							className="mt-4 rounded-md bg-red-500/10 p-3 text-center text-sm text-dashboard-pill-risk font-body"
						>
							{inlineError}
						</div>
					)}

					{/* Loading */}
					{showVerifying && (
						<div className="mt-4 text-center text-sm font-body text-dashboard-muted">
							{t("auth.verifying")}
						</div>
					)}

					{/* Resend */}
					<div className="mt-6 text-center">
						{resendTimer > 0 ? (
							<p
								className="text-sm font-body text-dashboard-muted"
								aria-live="polite"
							>
								{t("auth.resendOtpIn", { seconds: resendTimer })}
							</p>
						) : (
							<button
								ref={resendButtonRef}
								type="button"
								onClick={handleResend}
								disabled={isLoading}
								className="font-body text-sm text-dashboard-accent-dark hover:underline bg-transparent border-none cursor-pointer"
							>
								{isResending ? t("auth.sendingOtp") : t("auth.resendOtp")}
							</button>
						)}
					</div>

					{/* Back button */}
					<StudioButton
						type="button"
						variant="ghost"
						onClick={handleBack}
						style={{ width: "100%", marginTop: 16 }}
					>
						&larr; {t("auth.changePhoneNumber")}
					</StudioButton>

					{/* Dev hint */}
					{import.meta.env.VITE_SHOW_DEV_HINTS === "true" && (
						<p className="mt-4 text-center text-xs font-body text-dashboard-muted">
							Dev mode: Use OTP "123456"
						</p>
					)}
				</FloatPanel>
			</div>
		</div>
	);
}
